import { createSelector } from '../reselect';
import moment from 'moment';
import _ from 'lodash';
import { emptyArray, sumArray } from '../generalUtils';

const servicesToCheck = {
  // Use app constant
  GOOGLE_ADS: 'Google',
  FACEBOOK: 'Facebook',
  TIKTOK: 'TikTok',
  SNAPCHAT: 'Snapchat',
  PINTEREST: 'Pinterest',
};
export const selectConnectedServices = createSelector<any, Record<any, boolean>>(
  [
    (x) => x.isFacebookConnected,
    (x) => x.isGoogleAdsConnected,
    (x) => x.isGoogleAnalyticsConnected,
    (x) => x.isTiktokConnected,
    (x) => x.isSnapchatConnected,
    (x) => x.isKlaviyoConnected,
    (x) => x.isGorgiasConnected,
    (x) => x.isShipstationConnected,
    (x) => x.isPinterestConnected,
    (x) => x.isRechargeConnected,
    (x) => x.isEnquirelabsConnected,
    (x) => x.isKnoConnected,
    (x) => x.isFacebookSocialConnected,
    (x) => x.isSlackConnected,
    (x) => x.isAmazonConnected,
    (x) => x.isBingConnected,
  ],
  (
    FACEBOOK: boolean,
    GOOGLE_ADS: boolean,
    GOOGLE_ANALYTICS: boolean,
    TIKTOK: boolean,
    SNAPCHAT: boolean,
    KLAVIYO: boolean,
    GORGIAS: boolean,
    SHIPSTATION: boolean,
    PINTEREST: boolean,
    RECHARGE: boolean,
    ENQUIRELABS: boolean,
    KNO: boolean,
    FACEBOOK_SOCIAL: boolean,
    SLACK: boolean,
    AMAZON: boolean,
    BING: boolean
  ) => ({
    SHOPIFY: true,
    FACEBOOK,
    GOOGLE_ADS,
    GOOGLE_ANALYTICS,
    TIKTOK,
    SNAPCHAT,
    KLAVIYO,
    GORGIAS,
    SHIPSTATION,
    PINTEREST,
    RECHARGE,
    ENQUIRELABS,
    KNO,
    FACEBOOK_SOCIAL,
    SLACK,
    ALL_CHANNELS: true,
    'tiktok-ads': TIKTOK,
    'google-ads': GOOGLE_ADS,
    'facebook-ads': FACEBOOK,
    'pinterest-ads': PINTEREST,
    'snapchat-ads': SNAPCHAT,
    klaviyo: KLAVIYO,
    kno: KNO,
    enquirelabs: ENQUIRELABS,
    amazon: AMAZON,
    bing: BING,
  })
);

const getForecastServices = (connectedServices) =>
  Object.keys(servicesToCheck).filter((svc) => connectedServices[svc]);

const fillColumns = (data, columns, getVal) => {
  const dataByColumns = {};
  columns?.slice(1).forEach((column) => {
    const obj = data.find((d) => moment(d._id).utc().format('MMM DD, YYYY') === column);
    dataByColumns[column] = getVal(obj, column);
  });
  return dataByColumns;
};

const getGrossUnits = (product, velocity, lineItems, columns) => {
  const dataByColumns = {};
  let sum = 0;
  columns?.slice(1)?.forEach((column) => {
    sum = 0;
    const variants = Object.assign(
      {},
      ...Object.keys(product.variants).map((v_id) => {
        const velocityPresents = (velocity[column]?.variants?.[v_id] || 0) / 100;
        const value = Math.round(velocityPresents * lineItems[column]);
        sum += value;
        return { [v_id]: value };
      })
    );
    dataByColumns[column] = { variants, sum };
  });
  return dataByColumns;
};

const getLineItems = (orders, itemsPerOrder, columns) => {
  return fillColumns(
    [],
    columns,
    (obj, column) => (orders[column] || 0) * (itemsPerOrder[column] || 0)
  );
};

const getPaidMediaByColumns = (dataSource, dataField: string, columns) => {
  const [service, calculatedDataField] = dataField.split('-');

  // so we can add to this list if there are more fields that need to be computed in the future
  const computedFieldCalculators: { [key: string]: Function } = {
    'New Customers'(columnData: any): number {
      if (!columnData) return 0;

      const budget = columnData[service + '-Budget'],
        ncroas = columnData[service + '-New Customers ROAS'],
        aov = columnData[service + '-AOV'];

      if (!(budget && ncroas && aov)) return 0;

      return (budget * ncroas) / aov;
    },
  };

  const dataByColumns = {};
  columns?.slice(1)?.forEach((column) => {
    if (calculatedDataField in computedFieldCalculators) {
      const fieldCalculator = computedFieldCalculators[calculatedDataField];
      dataByColumns[column] = fieldCalculator(dataSource[column]);
    } else {
      dataByColumns[column] = dataSource[column]?.[dataField] || 0;
    }
  });

  return dataByColumns;
};

const getVelocityMixByColumns = (
  product,
  dataSource,
  id,
  columns,
  actualLineItems,
  productOrders
) => {
  const dataByColumns = {};
  let sum = 0;
  columns?.slice(1)?.forEach((column) => {
    sum = 0;
    const variants = Object.assign(
      {},
      ...Object.keys(product.variants).map((v_id) => {
        const projected = dataSource[column]?.mix?.[id]?.[v_id];
        const actual =
          ((productOrders.find((d) => moment(d._id).utc().format('MMM DD, YYYY') === column)
            ?.products?.[id]?.variants?.[v_id] |
            0) /
            actualLineItems[column]) *
          100;
        const value = projected || (!isNaN(actual) ? Math.round(actual) : 0);
        sum += value;
        return { [v_id]: value };
      })
    );

    dataByColumns[column] = { variants, sum };
  });
  return dataByColumns;
};

export const selectColumnHeaders = createSelector(
  [(state) => state.mainDatePickerSelectionRange, (state) => state.groupStatsBy],
  (date, groupBy) => {
    let cols = ['label'];
    if (!date) return cols;
    let temp = moment(date.start)?.clone();
    if (groupBy === 'month') {
      while (temp.isSameOrBefore(moment(date.end), 'month')) {
        cols.push(temp.format('MMMM YYYY'));
        temp.add(1, 'month');
      }
    } else {
      while (temp.isSameOrBefore(moment(date.end), 'day')) {
        cols.push(temp.format('MMM DD, YYYY'));
        temp.add(1, 'week');
      }
    }
    return cols;
  }
);

export const selectPaidMediaSumPerService = createSelector<any, any>(
  [(state) => state.forecast?.revenue?.PaidMedia, selectConnectedServices],
  (paidMedia, connectedServices) => {
    const res = {};
    getForecastServices(connectedServices).forEach((service) => {
      res[service] = _.mapValues(paidMedia, (data) => {
        return (data[`${service}-ROAS`] || 0) * (data[`${service}-Budget`] || 0);
      });
    });

    return res;
  }
);

export const selectPaidMediaSum = createSelector<any, any>(
  [selectPaidMediaSumPerService, selectColumnHeaders],
  (paidMediaSumPerService, columnHeaders) => {
    const res: { [key: string]: number } = {};
    _.forEach(columnHeaders?.slice(1), (date) => {
      _.forEach(paidMediaSumPerService, (service_sum) => {
        const sum = service_sum[date];
        res[date] = res[date] + sum || sum || 0;
      });
    });
    return res;
  }
);

export const selectRevenueSum = createSelector<any, any>(
  [selectPaidMediaSum],
  (paidMediaSum) => paidMediaSum
);

const getActualLineItems = (data, columns) => {
  return fillColumns(data, columns, (obj) =>
    sumArray(Object.values(obj?.products || {}).map((p: any) => p.sum_product_quantity) || {})
  );
};

const getOrdersProjected = (columns, paidMedia) => {
  const retVal = {};
  columns?.slice(1)?.forEach((column) => {
    const allOrders = sumArray(
      paidMedia.map((media) => {
        const aov = media.children.find((child) => child.label === 'AOV')?.[column] || 0;
        return aov * (media[column] || 0) === 0 ? 0 : Math.round(media[column] / aov);
      })
    );
    retVal[column] = allOrders;
  });
  return retVal;
};

const selectMappedPrepopulations = createSelector<any, any>(
  [(state) => state.forecast?.prepopulations],
  (prepopulations) => {
    if (!prepopulations || !Array.isArray(prepopulations)) return {};

    return prepopulations.reduce((acc, prepop) => {
      const { __v, rowKey, ...selectedFields } = prepop;
      acc[rowKey] = selectedFields;
      return acc;
    }, {});
  }
);

const selectPaidMedia = createSelector<any, any>(
  [
    selectConnectedServices,
    (state) => state.forecast?.revenue?.PaidMedia,
    selectColumnHeaders,
    selectPaidMediaSumPerService,
    selectMappedPrepopulations,
  ],
  (connectedServices, paidMediaData, columnHeaders, paidMediaSumPerService, prepopulations) =>
    getForecastServices(connectedServices).map((svc) => ({
      label: servicesToCheck[svc],
      value: `${svc}-Revenue`,
      section: 'revenue',
      children:[],
      symbol: '$',
      level: 3,
      ...paidMediaSumPerService[svc],
    }))
);

const selectOrdersProjected = createSelector<any, any>(
  [selectPaidMedia, selectColumnHeaders],
  (paidMedia, columnHeaders) => getOrdersProjected(columnHeaders, paidMedia)
);

const selectActualLineItems = createSelector<any, any>(
  [(state) => state.forecast?.productOrders, selectColumnHeaders],
  (productOrders, columnHeaders) => getActualLineItems(productOrders, columnHeaders)
);

const selectLineItemsPerOrder = createSelector<any, any>(
  [selectColumnHeaders, (state) => state.forecast?.velocity],
  (columnHeaders, velocity) => getPaidMediaByColumns(velocity, 'lineItemsPerOrder', columnHeaders)
);

const selectLineItems = createSelector<any, any>(
  [selectOrdersProjected, selectColumnHeaders, selectLineItemsPerOrder],
  (ordersProjected, columnHeaders, lineItemsPerOrder) =>
    getLineItems(ordersProjected, lineItemsPerOrder, columnHeaders)
);

const selectVelocityMixes = createSelector<any, any>(
  [
    (state) => state.forecast?.products,
    selectColumnHeaders,
    (state) => state.forecast?.velocity,
    selectActualLineItems,
    (state) => state.forecast?.productOrders,
  ],
  (products, columnHeaders, velocity, actualLineItems, productOrders) =>
    Object.assign(
      {},
      ...Object.keys(products).map((id) => ({
        [id]: getVelocityMixByColumns(
          products[id],
          velocity,
          id,
          columnHeaders,
          actualLineItems,
          productOrders
        ),
      }))
    )
);

const selectGrossUnits = createSelector<any, [{ sum: string; variants: any }]>(
  [(state) => state.forecast?.products, selectColumnHeaders, selectVelocityMixes, selectLineItems],
  (products, columnHeaders, velocityMixes, lineItems) =>
    Object.assign(
      {},
      ...Object.keys(products).map((id) => ({
        [id]: getGrossUnits(products[id], velocityMixes[id], lineItems, columnHeaders),
      }))
    )
);

export const selectSortedProducts = createSelector(
  [
    (state) => state.forecast?.inventory?.sortBy,
    (state) => state.forecast?.products,
    (state) => state.currentInventory,
    (state) => state.forecast?.inventory?.metaData,
  ],
  (sortBy, products, currentInventory, metaData: [{ avgSalesPerDay: number }]) => {
    let sortedProducts;
    switch (sortBy) {
      case 'velocityAsc':
        sortedProducts = _.orderBy(
          Object.values(products).map((x: any) => ({
            ...x,
            quantity: Math.round(metaData[x.productId]?.avgSalesPerDay) ?? 0,
          })),
          'quantity',
          'asc'
        )?.map((prod) => prod);
        break;
      case 'velocityDesc':
        sortedProducts = _.orderBy(
          Object.values(products).map((x: any) => ({
            ...x,
            quantity: Math.round(metaData[x.productId]?.avgSalesPerDay) ?? 0,
          })),
          'quantity',
          'desc'
        )?.map((prod) => prod);
        break;
      case 'prodAZ':
        sortedProducts = _.orderBy(Object.values(products), 'productTitle', 'asc')?.map(
          (prod) => prod
        );
        break;
      case 'prodZA':
        sortedProducts = _.orderBy(Object.values(products), 'productTitle', 'desc')?.map(
          (prod) => prod
        );
        break;
      case 'skuAsc':
        sortedProducts = _.orderBy(Object.values(products), 'productIdd', 'asc')?.map(
          (prod) => prod
        );
        break;
      case 'skuDesc':
        sortedProducts = _.orderBy(Object.values(products), 'productId', 'desc')?.map(
          (prod) => prod
        );
        break;
      case 'stockAsc':
        sortedProducts = _.orderBy(
          Object.values(products).map((x: any) => ({
            ...x,
            inventory: currentInventory[x.productId]?.sum_inventory_quantity ?? 0,
          })),
          'inventory',
          'asc'
        )?.map((prod) => prod);
        break;
      case 'stockDesc':
        sortedProducts = _.orderBy(
          Object.values(products).map((x: any) => ({
            ...x,
            inventory: currentInventory[x.productId]?.sum_inventory_quantity ?? 0,
          })),
          'inventory',
          'desc'
        )?.map((prod) => prod);
        break;
      default:
        sortedProducts = Object.values(products)?.map((prod) => prod);
        break;
    }
    return sortedProducts;
  }
);

export const selectForecastTableContent = createSelector<any, any>(
  [
    selectSortedProducts,
    selectPaidMedia,
    selectOrdersProjected,
    selectLineItems,
    selectLineItemsPerOrder,
    selectVelocityMixes,
    selectGrossUnits,
    selectPaidMediaSum,
    selectRevenueSum,
  ],
  (
    products,
    paidMedia,
    ordersProjected,
    lineItems,
    lineItemsPerOrder,
    velocityMixes,
    grossUnits,
    paidMediaSum,
    revenueSum
  ) => {
    const data = [
      {
        label: 'Revenue',
        value: 'revenue',
        children: [
          {
            label: 'Paid Media',
            value: 'paidMedia',
            children: paidMedia,
            level: 2,
            symbol: '$',
            section: 'revenue',
            ...paidMediaSum,
          },
        ],
        level: 1,
        symbol: '$',
        section: 'revenue',
        ...revenueSum,
      },
      {
        label: 'Velocity of Sales',
        value: 'velocity',
        section: 'velocity',
        children: [
          {
            label: 'Line Items',
            value: 'lineItems',
            level: 2,
            section: 'velocity',
            ...lineItems,
            children: [
              {
                label: 'Orders',
                value: 'orders',
                level: 3,
                section: 'velocity',
                ...ordersProjected,
              },
              {
                label: 'Line items per order',
                value: 'lineItemsPerOrder',
                level: 3,
                status: 'EDIT',
                section: 'velocity',
                ...lineItemsPerOrder,
              },
            ],
          },
          {
            label: 'Mix',
            value: 'mix',
            section: 'velocity',
            children:
              ((Object.values(products) || []) as any)?.map(
                ({ productTitle, productId, variants }) => {
                  const sumByDates = Object.assign(
                    {},
                    ...Object.keys(velocityMixes[productId]).map((key) => ({
                      [key]: velocityMixes[productId][key].sum,
                    }))
                  );
                  return {
                    label: productTitle,
                    value: `VelocityMix-${productId}`,
                    level: 3,
                    symbol: '%',
                    section: 'velocity',
                    children: Object.values(variants || {})?.map(
                      ({ variantTitle, variantId }: any, i) => {
                        const variantsByDates = Object.assign(
                          {},
                          ...Object.keys(velocityMixes[productId]).map((key) => ({
                            [key]: velocityMixes[productId][key].variants?.[variantId],
                          }))
                        );
                        return {
                          label: variantTitle,
                          value: `VelocityMix-${productId}-${variantId}`,
                          key: variantId,
                          sku: variantId,
                          level: 4,
                          symbol: '%',
                          section: 'velocity',
                          status: 'EDIT',
                          ...variantsByDates,
                        };
                      }
                    ),
                    ...sumByDates,
                  };
                }
              ) || emptyArray,
            level: 2,
          },
          {
            label: 'Gross units',
            value: 'grossUnits',
            level: 2,
            section: 'velocity',
            children:
              ((Object.values(products) || []) as any)?.map(
                ({ productTitle, productId, variants }) => {
                  const sumByDates = Object.assign(
                    {},
                    ...Object.keys(grossUnits[productId]).map((key) => ({
                      [key]: grossUnits[productId][key].sum,
                    }))
                  );
                  return {
                    label: productTitle,
                    value: productId,
                    level: 3,
                    section: 'velocity',
                    ...sumByDates,
                    children: Object.values(variants || {})?.map(
                      ({ variantTitle, variantId }: any, i) => {
                        const variantsByDates = Object.assign(
                          {},
                          ...Object.keys(grossUnits[productId]).map((key) => ({
                            [key]: grossUnits[productId][key].variants?.[variantId],
                          }))
                        );
                        return {
                          label: variantTitle,
                          value: `${productId}-${variantId}`,
                          key: i,
                          sku: variantId,
                          level: 4,
                          section: 'velocity',
                          ...variantsByDates,
                        };
                      }
                    ),
                  };
                }
              ) || emptyArray,
          },
        ],
        level: 1,
      },
    ];

    return data;
  }
);

export const metaDataOptions = [
  //move to types
  { label: 'Lead Time', value: 'leadTime', edit: 'true' },
  {
    label: 'Current average sales/day',
    value: 'avgSalesPerDay',
    edit: 'false',
  },
  { label: 'Days of stock on hand', value: 'daysOfStock', edit: 'false' },
  { label: 'Minimum days of stock', value: 'minDaysOfStock', edit: 'true' },
];

const getInventoryByDates = (column, currInventory, pastGrossUnits, accumulateGrossUnit) => {
  const startOfCurrentWeek = moment().startOf('week');
  if (
    moment(column).utc().isBefore(startOfCurrentWeek, 'day') &&
    column !== startOfCurrentWeek.format('MMM DD, YYYY')
  ) {
    return 'N/A';
  } else {
    return currInventory - pastGrossUnits - accumulateGrossUnit;
  }
};
export const selectInventoryProjected = createSelector<any, any>(
  [
    selectColumnHeaders,
    selectGrossUnits,
    (state) => state.forecast?.products,
    (state) => state.currentInventory,
    (state) => state.forecast?.accumulateGrossUnits,
  ],
  (columns, grossUnits, products, currentInventory, accumulateGrossUnits) => {
    let sum;

    return Object.assign(
      {},
      ...Object.keys(products).map((id) => {
        const inventory = {};
        columns?.slice(1)?.forEach((column) => {
          sum = getInventoryByDates(
            column,
            currentInventory[id]?.sum_inventory_quantity,
            sumArray(
              Object.keys(grossUnits[id])
                .filter((date) => moment(date).isBefore(moment(column), 'day'))
                .map((x) => grossUnits[id][x]?.sum || 0)
            ),
            accumulateGrossUnits[id]?.sum || 0
          );

          const variants = Object.assign(
            {},
            ...Object.keys(products[id].variants).map((v_id) => {
              const value = getInventoryByDates(
                column,
                currentInventory[id]?.variants?.[v_id],
                sumArray(
                  Object.keys(grossUnits[id])
                    .filter((date) => moment(date).isBefore(moment(column), 'day'))
                    .map((x) => grossUnits[id][x]?.variants?.[v_id] || 0)
                ),
                accumulateGrossUnits[id]?.variants?.[v_id] || 0
              );
              return { [v_id]: value };
            })
          );
          inventory[column] = { variants, sum };
        });

        return {
          [id]: inventory,
        };
      })
    );
  }
);

export const selectInventoryTableContent = createSelector<any, any>(
  [
    (state) => state.forecast?.inventory?.metaDataCol,
    (state) => state.forecast?.inventory?.metaData,
    selectSortedProducts,
    selectInventoryProjected,
  ],
  (metaDataCol, metaData, sortedProducts, inventoryProjected) => {
    const data = [
      {
        label: 'Inventory',
        value: 'inventory',
        section: 'inventory',
        metaData: metaDataOptions.find((x) => x.value === metaDataCol)?.label,
        level: 1,
        children:
          sortedProducts?.map(({ productTitle, productId, variants }: any, i) => {
            const sumByDates = Object.assign(
              {},
              ...Object.keys(inventoryProjected[productId]).map((key) => ({
                [key]: inventoryProjected[productId][key].sum,
              }))
            );
            return {
              label: productTitle,
              value: `inventory-${productId}`,
              level: 2,
              section: 'inventory',
              ...sumByDates,
              metaData: metaData?.[productId]?.[metaDataCol] || 0,
              children:
                Object.values(variants || {})?.map(({ variantId, variantTitle }: any, i) => {
                  const variantsByDates = Object.assign(
                    {},
                    ...Object.keys(inventoryProjected[productId]).map((key) => ({
                      [key]: inventoryProjected[productId][key].variants?.[variantId],
                    }))
                  );
                  return {
                    label: variantTitle,
                    value: `${productId}-${variantId}`,
                    sku: variantId,
                    level: 3,
                    section: 'inventory',
                    ...variantsByDates,
                    metaData: metaData?.[productId]?.variants?.[variantId]?.[metaDataCol] || 0,
                  };
                }) || emptyArray,
            };
          }) || emptyArray,
      },
    ];
    return data;
  }
);

export default {
  SELECTORS: {
    selectColumnHeaders,
    selectPaidMedia,
    selectOrdersProjected,
    selectActualLineItems,
    selectLineItemsPerOrder,
    selectLineItems,
    selectVelocityMixes,
    selectGrossUnits,
    selectForecastTableContent,
    selectInventoryTableContent,
    selectSortedProducts,
  },
};
